import { useState } from 'react';
import { Dialog, DialogContent, DialogActions, Button, TextField, Box, Typography, Slider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

const DEFAULT_RATING = 5;

export default function FeedbackDialog({ open, onClose, clientId }) {

    const { t } = useTranslation()
    
    const [rating, setRating] = useState(DEFAULT_RATING)
    const [ratingTouched, setRatingTouched] = useState(false)
    const [message, setMessage] = useState('')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitted, setSubmitted] = useState(false)
    
    const feedbackUrl = window.conf?.DASHBOARD?.FEEDBACK_URL
    const canSubmit = ratingTouched || message.trim().length > 0

    const handleRatingChange = (_, newValue) => {
        setRating(newValue)
        setRatingTouched(true)
    }

    const handleSubmit = async () => {
        if (!canSubmit || !feedbackUrl) return
        setIsSubmitting(true)
        try {
            const response = await fetch(feedbackUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    client_id: clientId,
                    feedback_message: message.trim() || '',
                    rating: rating,
                    scope: 'dashboard_logging',
                    log_path: '/srv/data/sites/bremer/barmstedt/25029-famila/static/data/feedback_log'
                })
            })
            if (!response.ok) throw new Error(`Server error: ${response.status}`)
            setSubmitted(true)
            setTimeout(() => handleClose(), 1500)
        } catch (error) {
            console.error('[FeedbackDialog] error:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClose = () => {
        setRating(DEFAULT_RATING)
        setRatingTouched(false)
        setMessage('')
        setSubmitted(false)
        onClose()
    }

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogContent dividers>
                {submitted ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6">{t('pages.dashboard.feedback_thanks')}</Typography>
                    </Box>
                ) : (
                    <>
                        <Box sx={{ mb: 4 }}>
                            <Typography sx={{ mb: 2 }}>{t('pages.dashboard.feedback_question')}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 1 }}>
                                <ThumbDownIcon sx={{ color: ratingTouched && rating <= 3 ? '#f44336' : 'rgba(0,0,0,0.3)' }} />
                                <Slider
                                    value={rating}
                                    onChange={handleRatingChange}
                                    min={1}
                                    max={10}
                                    step={1}
                                    marks
                                    valueLabelDisplay="auto"
                                    sx={{
                                        color: ratingTouched
                                            ? rating <= 3 ? '#f44336'
                                                : rating <= 6 ? '#ff9800'
                                                : '#4caf50'
                                            : '#888',
                                        '& .MuiSlider-valueLabel': { backgroundColor: '#003366' }
                                    }}
                                />
                                <ThumbUpIcon sx={{ color: ratingTouched && rating >= 8 ? '#4caf50' : 'rgba(0,0,0,0.3)' }} />
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, px: 4 }}>
                                <Typography variant="caption" color="text.secondary">{t('pages.dashboard.feedback_low')}</Typography>
                                <Typography variant="caption" color="text.secondary">{t('pages.dashboard.feedback_high')}</Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Typography sx={{ mb: 1 }}>{t('pages.dashboard.feedback_improve')}</Typography>
                            <TextField
                                multiline
                                rows={4}
                                fullWidth
                                placeholder={t('pages.dashboard.feedback_placeholder')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                            />
                        </Box>
                    </>
                )}
            </DialogContent>
            {!submitted && (
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleClose}>{t('pages.dashboard.feedback_cancel')}</Button>
                    <Button
                        size="large"
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!canSubmit || isSubmitting}
                        sx={{
                            backgroundColor: '#4caf50',
                            '&:hover': { backgroundColor: '#388e3c' },
                            '&.Mui-disabled': { backgroundColor: 'rgba(76,175,80,0.3)' }
                        }}
                    >
                        {isSubmitting ? <CircularProgress size={24} /> : t('pages.dashboard.feedback_submit')}
                    </Button>
                </DialogActions>
            )}
        </Dialog>
    )
}
